import styled, { css, keyframes } from 'styled-components';
import { exFont } from '../../shared/typography';

// Your marks (tplA 979–1055). Also the step-layout pieces Samples and
// Results reuse (StepPage, StepHead, Card, TextButton, Spinner, Dialog*).

// ------------------------------------------------------------ step layout
export const StepPage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: ${({ $entry }) => ($entry ? '896px' : '1180px')};
  margin: 0 auto;
  padding: 28px 32px 40px;
  box-sizing: border-box;
`;

// tplA 988: Your marks keeps one 880px column for the options and the grid.
export const YourMarksPage = styled(StepPage)`
  max-width: 880px;
  padding: 28px 24px 44px;
  gap: 16px;
  line-height: normal;
`;

export const YourMarksTitle = styled.h2`
  ${exFont(700, 24)}
  margin: 0;
  color: var(--ex-ink);
`;

export const YourMarksSub = styled.p`
  ${exFont(400, 15)}
  margin: 8px 0 0;
  max-width: 640px;
  line-height: 1.55;
  color: var(--ex-ink-strong);
  text-wrap: pretty;
`;

export const StepHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

export const StepTitle = styled.h2`
  ${exFont(600, 'xxl')}
  margin: 0;
  color: var(--ex-ink);
`;

export const StepSub = styled.p`
  ${exFont(400, 'm')}
  margin: 4px 0 0;
  max-width: 720px;
  color: var(--ex-ink-2);
  line-height: 1.5;
`;

export const Card = styled.div`
  background: var(--ex-surface);
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-card);
  box-shadow: var(--ex-shadow-1);
`;

export const TextButton = styled.button`
  ${({ $alignEnd }) => $alignEnd && 'align-self: flex-end;'}
  ${exFont(600, 's')}
  padding: 0;
  border: none;
  background: none;
  color: ${({ $tone }) =>
    $tone === 'bad' ? 'var(--ex-bad-ink)' : 'var(--ex-purple)'};
  cursor: pointer;
  &:hover:not(:disabled) {
    text-decoration: underline;
  }
  &:disabled {
    color: var(--ex-ink-faint);
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  display: inline-block;
  flex: 0 0 auto;
  width: ${({ $size }) => $size || 16}px;
  height: ${({ $size }) => $size || 16}px;
  border: 2px solid var(--ex-purple-150);
  border-top-color: var(--ex-purple);
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const Chip = styled.span`
  ${exFont(600, 'xs')}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: var(--ex-radius-pill);
  color: var(--ex-purple);
  background: var(--ex-purple-100);
  white-space: nowrap;
`;

export const Banner = styled.div`
  ${exFont(500, 's')}
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: var(--ex-radius-item);
  color: ${({ $tone }) =>
    $tone === 'warn' ? 'var(--ex-warn-ink)' : 'var(--ex-info-ink)'};
  background: ${({ $tone }) =>
    $tone === 'warn' ? 'var(--ex-warn-bg)' : 'var(--ex-info-bg)'};
`;

export const Muted = styled.span`
  ${exFont(400, 's')}
  color: var(--ex-ink-muted);
`;

// ---------------------------------------------------------------- dialogs
// Text inside a ChoiceDialog body: the body sets the design's type
// (shared/ui/style.js DIALOG_SIZES), so this only spaces paragraphs.
export const DialogText = styled.p`
  font: inherit;
  color: inherit;
  margin: 0;
  & + & {
    margin-top: 8px;
  }
`;

// tplA 1723: the re-mark reason field.
export const DialogInput = styled.input`
  ${exFont(400, 14)}
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px;
  border: 1px solid var(--ex-line-strong);
  border-radius: 10px;
  color: var(--ex-ink);
  &:focus {
    border-color: var(--ex-purple);
    outline: none;
  }
`;

// tplA 1721: 40px rounded square beside the title; amber for warnings.
export const DialogIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  color: ${({ $tone }) => ($tone === 'warn' ? '#8a6d00' : 'var(--ex-purple)')};
  background: ${({ $tone }) =>
    $tone === 'warn' ? '#fdf7dc' : 'var(--ex-purple-100)'};
`;

// ------------------------------------------------------------ marker tabs
// tplA 990–991: underline tabs, shown once there is another marker.
export const TabsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  border-bottom: 1px solid var(--ex-line);
`;

export const MarkerTab = styled.div`
  display: inline-flex;
  align-items: center;
  height: 38px;
  box-sizing: border-box;
  margin-bottom: -1px;
  border-bottom: 2.5px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--ex-purple)' : '#3d3847')};
`;

export const MarkerTabButton = styled.button`
  ${exFont(600, 14)}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding: 0 0 0 14px;
  border: none;
  background: transparent;
  color: inherit;
  white-space: nowrap;
  cursor: pointer;
  &:last-child {
    padding-right: 14px;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: -2px;
  }
`;

export const Initials = styled.span`
  ${exFont(700, 10)}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  color: var(--white);
  background: ${({ $color }) => $color || 'var(--ex-purple)'};
`;

export const TabCount = styled.span`
  ${exFont(500, 12)}
  color: var(--ex-ink-2);
`;

export const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin: 0 8px 0 2px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #3d3847;
  opacity: 0.6;
  cursor: pointer;
  &:hover {
    opacity: 1;
    color: var(--ex-bad-ink);
    background: var(--ex-bad-bg);
  }
`;

export const ActiveMarkerTitle = styled.p`
  ${exFont(600, 15)}
  margin: 12px 0 0;
  color: var(--ex-ink);
`;

export const AddLinkRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const AddLink = styled.button`
  ${({ $inTabs }) => exFont(600, $inTabs ? 13.5 : 14)}
  margin-left: ${({ $inTabs }) => ($inTabs ? '6px' : '0')};
  padding: 0;
  border: none;
  background: none;
  color: var(--ex-purple);
  white-space: nowrap;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

export const AddMarkerRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: 6px;
`;

export const AddMarkerInput = styled.input`
  ${exFont(400, 13.5)}
  width: 180px;
  height: 34px;
  box-sizing: border-box;
  padding: 0 10px;
  border: 1.5px solid var(--ex-line-strong);
  border-radius: 9px;
  color: var(--ex-ink);
  &:focus {
    border-color: var(--ex-purple);
    outline: none;
  }
`;

export const AddMarkerButton = styled.button`
  ${exFont(600, 13)}
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 999px;
  background: var(--ex-purple);
  color: var(--white);
  cursor: pointer;
  &:disabled {
    background: var(--ex-disabled-bg);
    cursor: not-allowed;
  }
`;

export const AddMarkerCancel = styled.button`
  ${exFont(600, 13)}
  padding: 0;
  border: none;
  background: none;
  color: var(--ex-ink-2);
  cursor: pointer;
`;

export const AddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const InlineInput = styled.input`
  ${exFont(400, 's')}
  width: 240px;
  padding: 6px 12px;
  border: 1px solid var(--ex-line-strong);
  border-radius: var(--ex-radius-pill);
  &:focus {
    border-color: var(--ex-purple);
    outline: none;
  }
`;

// ----------------------------------------------------------- entry options
// tplA 1003–1011: the two ways in.
export const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
`;

export const OptionCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  padding: ${({ $entry, $wide }) =>
    $entry ? '22px' : $wide ? '18px 22px' : '12px 14px'};
  text-align: left;
  background: var(--ex-surface);
  border: ${({ $entry }) =>
    $entry ? '1px solid var(--ex-line)' : '1.5px solid var(--ex-line-strong)'};
  border-radius: 14px;
  box-shadow: none;
  cursor: pointer;
  color: var(--ex-ink);
  &:hover:not(:disabled) {
    border-color: var(--ex-purple);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
  ${({ $wide }) =>
    $wide &&
    css`
      align-items: center;
      border-color: #c9a8f5;
      background: #fbf8fe;
    `}
`;

export const OptionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  border-radius: 12px;
  color: var(--ex-purple);
  background: ${({ $tint }) => $tint || 'var(--ex-purple-100)'};
`;

export const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
`;

export const OptionTitle = styled.span`
  ${exFont(600, 15.5)}
  color: var(--ex-ink);
`;

export const OptionBody = styled.span`
  ${exFont(400, 13.5)}
  color: var(--ex-ink-strong);
  line-height: 1.5;
`;

export const OptionAction = styled.span`
  ${exFont(600, 's')}
  flex: 0 0 auto;
  color: var(--ex-purple);
`;

// tplA 1012: the template tip.
export const TemplateStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border: 1px solid #e9dffa;
  border-radius: 12px;
  background: #faf7fe;
  & > span:nth-child(2) {
    ${exFont(400, 14)}
    flex: 1;
    min-width: 220px;
    color: #3d3847;
  }
`;

export const TemplateButton = styled.button`
  ${exFont(600, 13.5)}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  box-sizing: border-box;
  padding: 0 16px;
  border: 1.5px solid var(--ex-purple);
  border-radius: 999px;
  background: var(--ex-surface);
  color: var(--ex-purple);
  white-space: nowrap;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: progress;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

export const CheckLabel = styled.label`
  ${exFont(400, 's')}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--ex-ink-2);
  cursor: pointer;
`;

// ------------------------------------------------------------- marks grid
export const GridCard = styled(Card)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const GridToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid var(--ex-line);
`;

export const GridTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-right: auto;
`;

export const GridTitle = styled.p`
  ${exFont(700, 15)}
  margin: 0;
`;

export const GridSub = styled.p`
  ${exFont(400, 12.5)}
  margin: 0;
  color: var(--ex-ink-2);
`;

export const SearchInput = styled.input`
  ${exFont(400, 's')}
  width: 180px;
  padding: 6px 12px;
  border: 1px solid var(--ex-line-strong);
  border-radius: var(--ex-radius-pill);
  &:focus {
    border-color: var(--ex-purple);
    outline: none;
  }
`;

export const Select = styled.select.attrs({ className: 'inset-select-arrow' })`
  ${exFont(400, 's')}
  padding: 6px 8px;
  border: 1px solid var(--ex-line-strong);
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface);
  color: var(--ex-ink);
  &:focus {
    border-color: var(--ex-purple);
    outline: none;
  }
`;

export const ZoomGroup = styled.div`
  ${exFont(500, 's')}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--ex-ink-2);
`;

export const ZoomButton = styled.button`
  ${exFont(600, 'm')}
  width: 28px;
  height: 28px;
  border: 1px solid var(--ex-line-strong);
  border-radius: 50%;
  background: var(--ex-surface);
  color: var(--ex-ink-strong);
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const GridScroll = styled.div`
  position: relative;
  overflow: auto;
  max-height: 560px;
`;

export const GridHeadRow = styled.div`
  ${exFont(600, 11)}
  position: sticky;
  top: 0;
  z-index: 1;
  display: grid;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--ex-surface);
  border-bottom: 1px solid var(--ex-line);
  color: var(--ex-ink-2);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const GridRowEl = styled.div`
  ${exFont(400, 's')}
  position: absolute;
  left: 0;
  right: 0;
  display: grid;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  border-bottom: 1px solid var(--ex-line);
  background: ${({ $error }) =>
    $error ? 'var(--ex-bad-bg)' : 'var(--ex-surface)'};
  box-sizing: border-box;
`;

export const RowName = styled.span`
  ${exFont(500, 14)}
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: var(--ex-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CellInput = styled.input`
  ${exFont(500, 's')}
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 4px 6px;
  text-align: center;
  border: 1px solid
    ${({ $over, $check }) =>
      $over
        ? 'var(--ex-bad)'
        : $check
        ? 'var(--ex-warn)'
        : 'var(--ex-line-strong)'};
  border-radius: 6px;
  color: ${({ $over }) => ($over ? 'var(--ex-bad-ink)' : 'var(--ex-ink)')};
  background: ${({ $over }) =>
    $over ? 'var(--ex-bad-bg)' : 'var(--ex-surface)'};
  &:focus {
    border-color: var(--ex-purple);
    outline: none;
  }
  &:disabled {
    color: var(--ex-ink-muted);
    background: var(--ex-surface-2);
  }
`;

export const CellMax = styled.span`
  ${exFont(500, 12.5)}
  flex: 0 0 auto;
  white-space: nowrap;
  color: var(--ex-ink-2);
`;

export const RowTotal = styled.span`
  ${exFont(800, 15)}
  text-align: right;
  color: ${({ $empty }) => ($empty ? 'var(--ex-ink-faint)' : 'var(--ex-ink)')};
`;

export const RowNote = styled.span`
  ${exFont(500, 'xs')}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ $tone }) =>
    $tone === 'bad' ? 'var(--ex-bad-ink)' : 'var(--ex-warn-ink)'};
`;

export const EmptyNote = styled.p`
  ${exFont(400, 's')}
  margin: 0;
  padding: 24px 16px;
  text-align: center;
  color: var(--ex-ink-muted);
`;

// ------------------------------------------------------------ match report
export const ReportList = styled.ul`
  ${exFont(400, 's')}
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 320px;
  overflow: auto;
`;

export const ReportItem = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface-2);
  color: var(--ex-ink);
`;

export const ReportHeading = styled.p`
  ${exFont(600, 's')}
  margin: 8px 0 0;
  color: var(--ex-ink-strong);
`;

export const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
`;
